import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, FormField, Heading, Text, TextInput } from "grommet";
import * as React from "react";
import Avatar from "react-avatar";
import StyledModal from "../components/StyledModel";
import { getStats, StatViewModel } from "../stats/StatsStore";
import { TeamViewModel } from "../teams/TeamsStore";
import { AddStatRequest, addStat, MatchStatViewModel, MatchParticipantViewModel, MatchViewModel, RemoveStatRequest, removeStat, updateMatchStat, UpdateStatRequest, AddMatchOutcomeRequest, addOutcome, MatchOutcomeViewModel, OutcomeType, RemoveMatchOutcomeRequest, removeOutcome } from "./MatchesStore";
import { StatColor, StatIcon } from "../stats/StatIcon";
import { RenderStat } from "../stats/StatRenderer";
import { useReferenceData } from "../reference/ReferenceStore";
import { Outcome, OutcomeColor, OutcomeIcon } from "./OutcomeIcon";
import { RenderOutcome } from "./OutcomeRenderer";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
const trophy = <FontAwesomeIcon icon={["fad", "trophy"]}></FontAwesomeIcon>;
const medal = <FontAwesomeIcon icon={["fad", "medal"]}></FontAwesomeIcon>;

const MatchResults = ({ match }: { match: MatchViewModel }) => {
    console.log('MatchResults component loading with match prop:', match);
    const [disabled, setDisabled] = React.useState(false);
    const [showAddStatModal, setShowAddStatModal] = React.useState(false);
    const [showRemoveStatModal, setShowRemoveStatModal] = React.useState(false);
    const [participantStat, setParticipantStat] = React.useState({} as MatchParticipantViewModel);
    const [statToRemove, setStatToRemove] = React.useState({} as MatchStatViewModel);
    const [stat, setStat] = React.useState({} as StatViewModel);
    const [value, setValue] = React.useState(0);
    const [statCreateError, setStatCreateError] = React.useState("");
    const [statRemoveError, setStatRemoveError] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [statTypes, setStatTypes] = React.useState([] as StatViewModel[]);

    // Outcome-related state
    const [showAddOutcomeModal, setShowAddOutcomeModal] = React.useState(false);
    const [showRemoveOutcomeModal, setShowRemoveOutcomeModal] = React.useState(false);
    const [participantOutcome, setParticipantOutcome] = React.useState({} as MatchParticipantViewModel);
    const [outcomeToRemove, setOutcomeToRemove] = React.useState({} as MatchOutcomeViewModel);
    const [description, setDescription] = React.useState("");
    const [outcomeType, setOutcomeType] = React.useState("");
    const [outcomeScore, setOutcomeScore] = React.useState(0);
    const [outcomeCreateError, setOutcomeCreateError] = React.useState("");
    const [outcomeRemoveError, setOutcomeRemoveError] = React.useState("");
    const [additionalOutcomes, setAdditionalOutcomes] = React.useState([] as any[]);
    
    // Stat filters
    const [showKills, setShowKills] = React.useState(true);
    const [showTimes, setShowTimes] = React.useState(true);
    const [showObjectives, setShowObjectives] = React.useState(true);
    
    // Outcome visibility toggle
    const [showOutcomes, setShowOutcomes] = React.useState(true);
    
    const outcomeTypes = useReferenceData("outcomeTypes") as OutcomeType[];

    React.useEffect(() => {
        // Just load the stat types
        getStats().then((statsData) => {
            setStatTypes(statsData.result);
        });
    }, []);

    const handleChange = async () => {
        // This would normally reload match data, but since match is a prop,
        // the parent component handles reloading
    };

    const startAddStat = (participant: MatchParticipantViewModel) => {
        setParticipantStat(participant);
        setShowAddStatModal(true);
    };

    const addStatToMatch = () => {
        if (!match) return;
        
        setStatCreateError("");
        setIsLoading(true);
        addStat({ matchID: match.id, participantID: participantStat.id, statID: stat.id, value } as AddStatRequest).then(async (response) => {
            if (response.success) {
                setStat({} as StatViewModel);
                setValue(0);
                await handleChange();
                setShowAddStatModal(false);
            } else {
                setStatCreateError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const startRemoveStat = (stat: MatchStatViewModel) => {
        setStatToRemove(stat);
        setShowRemoveStatModal(true);
    };

    const removeStatFromMatch = () => {
        if (!match) return;

        setStatRemoveError("");
        setIsLoading(true);
        removeStat({ matchID: match.id, matchStatID: statToRemove.id } as RemoveStatRequest).then(async (response) => {
            if (response.success) {
                await handleChange();
                setShowRemoveStatModal(false);
            } else {
                setStatRemoveError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const [showUpdateStatModal, setShowUpdateStatModal] = React.useState(false);
    const [statToUpdate, setStatToUpdate] = React.useState({} as MatchStatViewModel);
    const [statUpdateError, setStatUpdateError] = React.useState("");
    const [statUpdateValue, setStatUpdateValue] = React.useState(0);

    const startUpdateStat = (stat: MatchStatViewModel) => {
        setStatToUpdate(stat);
        setShowUpdateStatModal(true);
        setStatUpdateValue(stat.value);
    };

    const updateStat = () => {
        if (!match) return;

        setStatUpdateError("");
        updateMatchStat({
            matchID: match.id,
            matchStatID: statToUpdate.id,
            value: statUpdateValue,
        } as UpdateStatRequest).then(async (res) => {
            await handleChange();
            setShowUpdateStatModal(false);
        });
    };

    // Outcome-related functions
    const startAddOutcome = (participant: MatchParticipantViewModel) => {
        setParticipantOutcome(participant);
        setShowAddOutcomeModal(true);
    };

    const startChangeOutcome = async (participant: MatchParticipantViewModel) => {
        // Remove existing outcomes first before allowing change
        if (participant.outcomes.length > 0) {
            setOutcomeCreateError("");
            setIsLoading(true);
            try {
                const removePromises = participant.outcomes.map(outcome => 
                    removeOutcome({ matchID: match!.id, outcomeID: outcome.id } as RemoveMatchOutcomeRequest)
                );
                const responses = await Promise.all(removePromises);
                if (responses.every(r => r.success)) {
                    await handleChange();
                    setParticipantOutcome(participant);
                    setShowAddOutcomeModal(true);
                } else {
                    setOutcomeCreateError("Failed to remove existing outcome");
                }
            } catch (e) {
                setOutcomeCreateError("Failed to remove existing outcome");
            } finally {
                setIsLoading(false);
            }
        } else {
            startAddOutcome(participant);
        }
    };

    const addOutcomeToMatch = () => {
        if (!match) return;
        
        setOutcomeCreateError("");
        setIsLoading(true);
        addOutcome({ matchID: match.id, description, outcomeType, participantID: participantOutcome.id, score: outcomeScore } as AddMatchOutcomeRequest).then(async (response) => {
            if (response.success) {
                let additionalResponses = await Promise.all(additionalOutcomes.map((additionalOutcome) => addOutcome(additionalOutcome.request)));

                if (additionalResponses.every((response) => response.success)) {
                    setDescription("");
                    setOutcomeType("");
                    setOutcomeScore(0);
                    await handleChange();
                    setShowAddOutcomeModal(false);
                    setAdditionalOutcomes([]);
                } else {
                    setOutcomeCreateError(additionalResponses.map((r) => r.errors.join("\n")).join("\n"));
                }
            } else {
                setOutcomeCreateError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const startRemoveOutcome = (outcome: MatchOutcomeViewModel) => {
        setOutcomeToRemove(outcome);
        setShowRemoveOutcomeModal(true);
    };

    const removeOutcomeFromMatch = () => {
        if (!match) return;

        setOutcomeRemoveError("");
        setIsLoading(true);
        removeOutcome({ matchID: match.id, outcomeID: outcomeToRemove.id } as RemoveMatchOutcomeRequest).then(async (response) => {
            if (response.success) {
                await handleChange();
                setShowRemoveOutcomeModal(false);
            } else {
                setOutcomeRemoveError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const setOutcome = (outcomeType: OutcomeType) => {
        let addOutcomes = [] as any[];
        if (outcomeType.name == "Win") {
            match.participants.forEach((p) => {
                if (p.id != participantOutcome.id && p.outcomes.length == 0) {
                    addOutcomes.push({
                        team: p.team,
                        request: {
                            matchID: match.id,
                            description: "",
                            participantID: p.id,
                            outcomeType: "Loss",
                            score: 0,
                        } as AddMatchOutcomeRequest,
                    });
                }
            });
            setAdditionalOutcomes([...additionalOutcomes, ...addOutcomes]);
        } else {
            setAdditionalOutcomes([]);
        }
        setOutcomeType(outcomeType.name);
    };

    const updateAdditionalOutcome = (index: number, outcome: any) => {
        let copy = [...additionalOutcomes];
        copy[index] = { ...copy[index], request: { ...copy[index].request, ...outcome } };
        setAdditionalOutcomes(copy);
    };

    const getRankIcon = (rank: number) => {
        switch (rank) {
            case 1:
                return trophy;
            case 2:
            case 3:
                return medal;
            default:
                return null;
        }
    };

    if (!match) {
        console.log('No match data, showing loading...');
        return (
            <Box fill align="center" justify="center" pad="large">
                <Text>Loading match data...</Text>
            </Box>
        );
    }

    console.log('Match found:', match);
    console.log('Participants:', match.participants);

    // Sort participants by outcome score only (no stat summing)
    const sortedParticipants = match.participants
        .map(p => {
            // Get the primary outcome score (usually the first outcome)
            const primaryOutcome = p.outcomes.length > 0 ? p.outcomes[0] : null;
            const outcomeScore = primaryOutcome ? primaryOutcome.score : 0;
            
            return {
                participant: p,
                outcomeScore: outcomeScore,
                // Only use outcome score for ranking, no stat totals
                rankingScore: outcomeScore
            };
        })
        .sort((a, b) => b.rankingScore - a.rankingScore);

    return (
        <>
            {/* Stat Filters */}
            <Box
                background="dark-2"
                pad="medium"
                round="small"
                margin={{ bottom: "medium" }}
                direction="row"
                gap="medium"
                align="center"
                justify="between"
            >
                <Box direction="row" gap="medium" align="center">
                    <Text weight="bold" color="accent-1">Sort by Stats:</Text>
                    <Box direction="row" gap="medium">
                        <Box direction="row" gap="xsmall" align="center">
                            <input
                                type="checkbox"
                                checked={showKills}
                                onChange={(e) => setShowKills(e.target.checked)}
                                style={{ accentColor: 'var(--accent,#00aced)' }}
                            />
                            {/* Use the actual kill stat icon or fallback */}
                            <Box style={{ color: showKills ? "var(--accent,#00aced)" : "#666" }}>
                                {(() => {
                                    const killStat = match.participants.flatMap(p => p.stats).find(s => s.stat.name.toLowerCase().includes('kill'));
                                    if (killStat) {
                                        return <StatIcon {...killStat.stat} />;
                                    }
                                    return <FontAwesomeIcon icon={["fad", "crosshairs"]} />;
                                })()}
                            </Box>
                        </Box>
                        <Box direction="row" gap="xsmall" align="center">
                            <input
                                type="checkbox"
                                checked={showTimes}
                                onChange={(e) => setShowTimes(e.target.checked)}
                                style={{ accentColor: 'var(--accent,#00aced)' }}
                            />
                            {/* Use the actual time stat icon or fallback */}
                            <Box style={{ color: showTimes ? "var(--accent,#00aced)" : "#666" }}>
                                {(() => {
                                    const timeStat = match.participants.flatMap(p => p.stats).find(s => s.stat.name.toLowerCase().includes('time'));
                                    if (timeStat) {
                                        return <StatIcon {...timeStat.stat} />;
                                    }
                                    return <FontAwesomeIcon icon={["fad", "clock"]} />;
                                })()}
                            </Box>
                        </Box>
                        <Box direction="row" gap="xsmall" align="center">
                            <input
                                type="checkbox"
                                checked={showObjectives}
                                onChange={(e) => setShowObjectives(e.target.checked)}
                                style={{ accentColor: 'var(--accent,#00aced)' }}
                            />
                            {/* Use the actual objective stat icon or fallback */}
                            <Box style={{ color: showObjectives ? "var(--accent,#00aced)" : "#666" }}>
                                {(() => {
                                    const objectiveStat = match.participants.flatMap(p => p.stats).find(s => s.stat.name.toLowerCase().includes('objective'));
                                    if (objectiveStat) {
                                        return <StatIcon {...objectiveStat.stat} />;
                                    }
                                    return <FontAwesomeIcon icon={["fad", "bullseye"]} />;
                                })()}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                
                {/* Clear Score Buttons */}
                <Box direction="row" gap="small">
                    <Button
                        size="small"
                        label="Clear Outcomes"
                        onClick={() => {
                            // Clear all outcome scores
                            match.participants.forEach(participant => {
                                participant.outcomes.forEach(outcome => {
                                    removeOutcome({ matchID: match.id, outcomeID: outcome.id } as RemoveMatchOutcomeRequest);
                                });
                            });
                            handleChange();
                        }}
                        style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            background: 'rgba(255, 64, 64, 0.1)',
                            border: '1px solid rgba(255, 64, 64, 0.3)',
                            color: '#ff4040'
                        }}
                        disabled={isLoading}
                    />
                    <Button
                        size="small"
                        label="Clear Kills"
                        onClick={() => {
                            // Clear all kill stats
                            match.participants.forEach(participant => {
                                participant.stats
                                    .filter(stat => stat.stat.name.toLowerCase().includes('kill'))
                                    .forEach(stat => {
                                        removeStat({ matchID: match.id, matchStatID: stat.id } as RemoveStatRequest);
                                    });
                            });
                            handleChange();
                        }}
                        style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            background: 'rgba(255, 64, 64, 0.1)',
                            border: '1px solid rgba(255, 64, 64, 0.3)',
                            color: '#ff4040'
                        }}
                        disabled={isLoading}
                    />
                    <Button
                        size="small"
                        label="Clear Times"
                        onClick={() => {
                            // Clear all time stats
                            match.participants.forEach(participant => {
                                participant.stats
                                    .filter(stat => stat.stat.name.toLowerCase().includes('time'))
                                    .forEach(stat => {
                                        removeStat({ matchID: match.id, matchStatID: stat.id } as RemoveStatRequest);
                                    });
                            });
                            handleChange();
                        }}
                        style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            background: 'rgba(255, 64, 64, 0.1)',
                            border: '1px solid rgba(255, 64, 64, 0.3)',
                            color: '#ff4040'
                        }}
                        disabled={isLoading}
                    />
                    <Button
                        size="small"
                        label="Clear Objectives"
                        onClick={() => {
                            // Clear all objective stats
                            match.participants.forEach(participant => {
                                participant.stats
                                    .filter(stat => stat.stat.name.toLowerCase().includes('objective'))
                                    .forEach(stat => {
                                        removeStat({ matchID: match.id, matchStatID: stat.id } as RemoveStatRequest);
                                    });
                            });
                            handleChange();
                        }}
                        style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            background: 'rgba(255, 64, 64, 0.1)',
                            border: '1px solid rgba(255, 64, 64, 0.3)',
                            color: '#ff4040'
                        }}
                        disabled={isLoading}
                    />
                </Box>
            </Box>

            {/* Team Rankings Section */}
            <Box
                background="dark-2"
                pad="medium"
                round="small"
                gap="medium"
                style={{
                    boxShadow: '0 0 20px rgba(var(--accent-rgb), 0.1)'
                }}
                margin={{ bottom: "medium" }}
            >
                <Box 
                    direction="row" 
                    justify="between" 
                    align="center"
                    pad={{ bottom: "medium" }}
                    border={{ side: 'bottom', color: 'accent-1', size: '1px' }}
                >
                    <Text 
                        size="xlarge" 
                        weight="bold"
                        color="accent-1"
                        style={{
                            textTransform: 'uppercase',
                            letterSpacing: '2px'
                        }}
                    >
                        Team Rankings
                    </Text>
                    <Button
                        label={showOutcomes ? "Hide Outcomes" : "Show Outcomes"}
                        onClick={() => setShowOutcomes(!showOutcomes)}
                        size="small"
                        style={{
                            background: showOutcomes ? 'rgba(var(--accent-rgb), 0.2)' : 'rgba(var(--accent-rgb), 0.1)',
                            border: '1px solid rgba(var(--accent-rgb), 0.3)',
                            color: 'var(--accent,#00aced)'
                        }}
                        hoverIndicator="light-1"
                    />
                </Box>

                <Box gap="small">
                    {sortedParticipants.map((item, index) => (
                        <Box
                            key={item.participant.id}
                            direction="row"
                            align="center"
                            justify="between"
                            background="dark-1"
                            pad="medium"
                            round="small"
                            onClick={() => !disabled && startAddStat(item.participant)}
                            style={{
                                cursor: disabled ? 'default' : 'pointer',
                                border: '1px solid rgba(var(--accent-rgb), 0.2)',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <Box direction="row" align="center" gap="medium">
                                <Box 
                                    width="32px"
                                    height="32px"
                                    round="50%"
                                    align="center"
                                    justify="center"
                                    style={{
                                        background: index === 0 ? 'linear-gradient(135deg, #FFD700, #FFA500)' : 
                                                   index === 1 ? 'linear-gradient(135deg, #C0C0C0, #A9A9A9)' :
                                                   index === 2 ? 'linear-gradient(135deg, #CD7F32, #8B4513)' :
                                                   'linear-gradient(135deg, var(--accent-1), var(--accent-2))',
                                        boxShadow: index === 0 ? '0 0 10px rgba(255, 215, 0, 0.3)' :
                                                  index === 1 ? '0 0 10px rgba(192, 192, 192, 0.3)' :
                                                  index === 2 ? '0 0 10px rgba(205, 127, 50, 0.3)' :
                                                  '0 0 10px rgba(var(--accent-rgb), 0.2)'
                                    }}
                                >
                                    {getRankIcon(index + 1) || <Text weight="bold">{index + 1}</Text>}
                                </Box>

                                <Box 
                                    width="48px" 
                                    height="48px" 
                                    style={{
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: '2px solid var(--accent,#00aced)'
                                    }}
                                >
                                    <img 
                                        src={item.participant.team?.logoUri} 
                                        alt={item.participant.team?.name} 
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Text 
                                        weight="bold" 
                                        size="large"
                                        style={{
                                            color: item.participant.team?.color || 'var(--accent,#00aced)'
                                        }}
                                    >
                                        {item.participant.team?.name}
                                    </Text>
                                    {/* Show outcomes for this team - conditional on toggle */}
                                    {showOutcomes && (
                                        <Box direction="row" gap="xsmall" margin={{ top: "xsmall" }}>
                                            {item.participant.outcomes.map((outcome, outcomeIndex) => (
                                                <Box
                                                    key={outcomeIndex}
                                                    direction="row"
                                                    align="center"
                                                    gap="xsmall"
                                                    background="dark-2"
                                                    pad={{ horizontal: "small", vertical: "xsmall" }}
                                                    round="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!disabled) {
                                                            startRemoveOutcome(outcome);
                                                        }
                                                    }}
                                                    style={{ 
                                                        cursor: disabled ? 'default' : 'pointer',
                                                        border: '1px solid rgba(var(--accent-rgb), 0.3)'
                                                    }}
                                                >
                                                    <Box style={{ color: OutcomeColor(outcome) }}>
                                                        {OutcomeIcon(outcome)}
                                                    </Box>
                                                    <Text size="small" weight="bold" color={OutcomeColor(outcome)}>
                                                        {outcome.score}
                                                    </Text>
                                                </Box>
                                            ))}
                                            {/* Show Add or Change button based on whether outcomes exist */}
                                            {!disabled && (
                                                item.participant.outcomes.length === 0 ? (
                                                    <Button
                                                        size="small"
                                                        label="Add Outcome"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startAddOutcome(item.participant);
                                                        }}
                                                        style={{
                                                            fontSize: '12px',
                                                            padding: '4px 8px',
                                                            background: 'rgba(var(--accent-rgb), 0.1)',
                                                            border: '1px solid rgba(var(--accent-rgb), 0.3)'
                                                        }}
                                                    />
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        label="Change"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startChangeOutcome(item.participant);
                                                        }}
                                                        style={{
                                                            fontSize: '12px',
                                                            padding: '4px 8px',
                                                            background: 'rgba(var(--accent-rgb), 0.1)',
                                                            border: '1px solid rgba(var(--accent-rgb), 0.3)'
                                                        }}
                                                    />
                                                )
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            <Box 
                                direction="row"
                                align="center"
                                gap="medium"
                            >
                                {item.participant.stats
                                    .filter(stat => {
                                        const statName = stat.stat.name.toLowerCase();
                                        if (statName.includes('kill') && !showKills) return false;
                                        if (statName.includes('time') && !showTimes) return false;
                                        if (statName.includes('objective') && !showObjectives) return false;
                                        return true;
                                    })
                                    .map((stat, statIndex) => (
                                    <Box 
                                        key={statIndex}
                                        background="dark-2"
                                        pad={{ horizontal: "medium", vertical: "small" }}
                                        round="medium"
                                        style={{
                                            border: '1px solid rgba(var(--accent-rgb), 0.3)',
                                            cursor: disabled ? 'default' : 'pointer'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!disabled) {
                                                startUpdateStat(stat);
                                            }
                                        }}
                                    >
                                        <Box direction="row" align="center" gap="xsmall">
                                            <StatIcon {...stat.stat} />
                                            <Text 
                                                weight="bold" 
                                                size="medium"
                                                color="accent-1"
                                            >
                                                {stat.value}
                                            </Text>
                                        </Box>
                                    </Box>
                                ))}
                                {/* Show only outcome score as the main score */}
                                <Box 
                                    background="dark-2"
                                    pad={{ horizontal: "medium", vertical: "small" }}
                                    round="medium"
                                    style={{
                                        border: '2px solid var(--accent,#00aced)',
                                        background: 'linear-gradient(135deg, rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.05))'
                                    }}
                                >
                                    <Text 
                                        weight="bold" 
                                        size="xlarge"
                                        color="accent-1"
                                    >
                                        {item.outcomeScore}
                                    </Text>
                                </Box>
                                {!disabled && (
                                    <Button
                                        icon={plus}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            startAddStat(item.participant);
                                        }}
                                        style={{
                                            padding: '8px',
                                            borderRadius: '50%',
                                            background: 'rgba(var(--accent-rgb), 0.1)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        hoverIndicator="light-1"
                                    />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            <StyledModal isOpen={showAddStatModal} setIsOpen={setShowAddStatModal}>
                <Box width={"medium"}>
                    <Box direction="row" align="center" gap="small">
                        <img src={participantStat?.team?.logoUri} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {participantStat?.team?.name}
                    </Box>
                    <Box gap="small" pad="small">
                        {statTypes.map((_stat, statTypeIndex) => (
                            <Button key={statTypeIndex} primary={stat.id == _stat.id} color={StatColor(_stat)} label={_stat.name} icon={StatIcon(_stat)} onClick={() => setStat(_stat)} disabled={isLoading}></Button>
                        ))}
                    </Box>
                    <FormField label="Value">
                        <TextInput type={"number"} value={value} onChange={(e) => setValue(+e.target.value)} />
                    </FormField>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statCreateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowAddStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => addStatToMatch()} label="Add" disabled={isLoading || !stat.id}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showUpdateStatModal} setIsOpen={setShowUpdateStatModal}>
                <Box width={"medium"}>
                    <Heading size="small">{statToUpdate?.stat?.name}</Heading>
                    <FormField label="Value">
                        <TextInput type={"number"} value={statUpdateValue} onChange={(e) => setStatUpdateValue(+e.target.value)} />
                    </FormField>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statUpdateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowUpdateStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => updateStat()} label="Update" disabled={isLoading}></Button>
                    </Box>
                    <Button onClick={() => startRemoveStat(statToUpdate)} label="Remove Stat" color="status-critical" disabled={isLoading}></Button>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveStatModal} setIsOpen={setShowRemoveStatModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this stat?</Text>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => removeStatFromMatch()} label="Remove" color="status-critical" disabled={isLoading}></Button>
                    </Box>
                </Box>
            </StyledModal>

            {/* Outcome Modals */}
            <StyledModal isOpen={showAddOutcomeModal} setIsOpen={setShowAddOutcomeModal}>
                <Box width={"medium"}>
                    <Box direction="row" align="center" gap="small">
                        <img src={participantOutcome?.team?.logoUri} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {participantOutcome?.team?.name}
                    </Box>
                    <Box gap="small" pad="small">
                        {outcomeTypes?.map((_outcomeType, outcomeTypeIndex) => (
                            <Button
                                key={outcomeTypeIndex}
                                primary={outcomeType == _outcomeType.name}
                                color={OutcomeColor({ outcomeType: _outcomeType.name })}
                                label={_outcomeType.displayName}
                                icon={OutcomeIcon({ outcomeType: _outcomeType.name })}
                                onClick={() => setOutcome(_outcomeType)}
                                disabled={isLoading}
                            />
                        ))}
                    </Box>
                    <FormField label="Description">
                        <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
                    </FormField>
                    <FormField label="Score">
                        <TextInput type={"number"} value={outcomeScore} onChange={(e) => setOutcomeScore(+e.target.value)} />
                    </FormField>
                    {additionalOutcomes.map(({ team, request }, i) => (
                        <div key={i}>
                            <Box direction="row" align="center" gap="small">
                                <img src={team.logoUri} alt="" style={{ height: "45px", width: "45px", borderRadius: "5px", objectFit: "contain" }} />
                                <Box direction="row" align="center" gap="xsmall">
                                    {OutcomeIcon({ outcomeType: request.outcomeType })}
                                    <Text>{request.outcomeType}</Text>
                                </Box>
                                <TextInput type={"number"} value={request.score} onChange={(e) => updateAdditionalOutcome(i, { score: +e.target.value })} />
                            </Box>
                            <TextInput value={request.description} onChange={(e) => updateAdditionalOutcome(i, { description: e.target.value })} />
                        </div>
                    ))}
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{outcomeCreateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowAddOutcomeModal(false)} label="Cancel" disabled={isLoading} />
                        <Button primary onClick={() => addOutcomeToMatch()} label="Add" disabled={isLoading || !outcomeType} />
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveOutcomeModal} setIsOpen={setShowRemoveOutcomeModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this outcome?</Text>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{outcomeRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveOutcomeModal(false)} label="Cancel" disabled={isLoading} />
                        <Button primary onClick={() => removeOutcomeFromMatch()} label="Remove" color="status-critical" disabled={isLoading} />
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};

export default MatchResults;